import cn from 'classnames';
import { memo } from 'react';
import { CgArrowUpR } from 'react-icons/cg';
import { GoGear } from 'react-icons/go';
import { IoShareSocialSharp } from 'react-icons/io5';

import { CalculatorStopIcon, RecordingIcon } from '@src/assets/icons';
import { PanelistInviteModal, ShareRoomModal } from '@src/components';
import { useDocumentPiP } from '@src/components/FloatingDocumentPIPWindow';
import { useCalculatorActions } from '@src/hooks';
import { OTooltip } from '@src/shared';

import useRoomHeader from './useRoomHeader';
import './roomHeader.scss';
import CollapsibleAccordion from './CollapsibleAccordion';
import { EBannerType } from './RoomHeader.type';

const RoomHeader = () => {
  const { handleStopSharing } = useCalculatorActions();
  const {
    isMobile,
    roomTitle,
    roomId,
    shareButtonRef,
    handleShareRoomClick,
    screenSharerName,
    sharedCalculatorData,
    havePermissionToStopCalculator,
    sharerName,
    isScreenRecordActive,
    handleRecordAction,
    havePermissionToStopRecording,
    isRoomSettingsOpen,
    setIsRoomSettingsOpen,
    meetingType,
    havePermissionToStopPresenting,
    handleStopPresenting,
    expandedAccordion,
    handleToggleAccordion,
    canClose,
    isAnnotating,
    annotatorCount,
    isCurrentUserSharing,
    isHost,
  } = useRoomHeader();

  useDocumentPiP();

  return (
    <div className={cn('meet-room-header', { mobile: isMobile })} style={{ backgroundColor: 'var(--dark-black)' }}>
      <div className="left-side">
        <label className="header-title" style={{ color: 'var(--font-color)' }}>
          {roomTitle || roomId}
        </label>
        <OTooltip title="Share meeting link" placement="right">
          <div
            id="room-header-share-button"
            ref={shareButtonRef}
            className="icon-container"
            onClick={() => handleShareRoomClick()}
          >
            <IoShareSocialSharp width={24} height={24} className={cn('text-white', { blue: isMobile })} />
          </div>
        </OTooltip>
      </div>
      <div id="pip" />
      <div>
        {!isMobile && (
          <div className="header-settings-container">
            {screenSharerName && (
              <CollapsibleAccordion
                isExpanded={expandedAccordion === EBannerType.SHARING_ANNOTATING}
                onToggle={() => handleToggleAccordion(EBannerType.SHARING_ANNOTATING)}
                canClose={canClose}
                icon={
                  <div className="screen-share-icon-wrapper">
                    <CgArrowUpR className="screen-share-icon" color="var(--font-color)" />
                  </div>
                }
                text={`${screenSharerName}${isCurrentUserSharing ? ' (You)' : ''} is presenting${isAnnotating && (isCurrentUserSharing || isHost) ? ` & annotating${annotatorCount > 0 ? ` with ${annotatorCount} other${annotatorCount > 1 ? 's' : ''}` : ''}` : ''}`}
                buttonText="Stop Presenting"
                onButtonClick={handleStopPresenting}
                showButton={havePermissionToStopPresenting}
              />
            )}
            {sharedCalculatorData.isOpen && (
              <CollapsibleAccordion
                isExpanded={expandedAccordion === EBannerType.CALCULATOR}
                onToggle={() => handleToggleAccordion(EBannerType.CALCULATOR)}
                canClose={canClose}
                icon={<CalculatorStopIcon />}
                text={`${sharerName} is sharing calculator`}
                buttonText="Stop Calculator"
                onButtonClick={handleStopSharing}
                showButton={havePermissionToStopCalculator}
              />
            )}

            {isScreenRecordActive && (
              <CollapsibleAccordion
                isExpanded={expandedAccordion === EBannerType.RECORDING}
                onToggle={() => handleToggleAccordion(EBannerType.RECORDING)}
                canClose={canClose}
                icon={<RecordingIcon />}
                text="Recording"
                buttonText="Stop Recording"
                onButtonClick={handleRecordAction}
                showButton={havePermissionToStopRecording}
                buttonVariant="secondary"
                buttonOutlined={true}
              />
            )}

            <div className="divider" style={{ backgroundColor: 'var(--border-color)' }} />
            <OTooltip title="Settings" placement="left">
              <div
                className={cn('icon-container', { active: isRoomSettingsOpen })}
                onClick={() => setIsRoomSettingsOpen(!isRoomSettingsOpen)}
              >
                <GoGear height={16} width={16} />
              </div>
            </OTooltip>
          </div>
        )}
      </div>
      <PanelistInviteModal shareButtonRef={meetingType === 'webinar' ? shareButtonRef : null} />
      <ShareRoomModal shareButtonRef={meetingType !== 'webinar' ? shareButtonRef : null} />
    </div>
  );
};

export default memo(RoomHeader);
